import { Avatar, Modal, SxProps } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { slashDateFormat } from 'Config';
import { he } from 'date-fns/locale';
import { useIsDesktop } from 'Hooks';
import { useState } from 'react';
import {
  AccordionArrowBlue,
  PurpleDateIcon,
  LeftArrowDatePicker,
  RightArrowDatePicker,
} from 'Images';

interface IDatePickerButton {
  iconSrc: string;
  iconAlt?: string;
  iconSx?: SxProps;
  onDateSelect: (date: Date) => void;
  maxDate?: Date;
  minDate?: Date;
  disablePast?: boolean;
}

export const DatePickerButton = ({
  iconSrc,
  iconAlt = '',
  iconSx,
  onDateSelect,
  maxDate,
  minDate,
  disablePast = true,
}: IDatePickerButton) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const isDesktop = useIsDesktop();

  const handleClick = () => {
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleDateChange = (newValue: Date | null) => {
    if (newValue) {
      setSelectedDate(newValue);
      onDateSelect(newValue);
      handleClose();
    }
  };

  return (
    <>
      <Avatar
        src={iconSrc}
        alt={iconAlt}
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          ...iconSx,
        }}
      />
      <Modal
        open={isDialogOpen}
        onClose={handleClose}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            outline: 'none',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
            <DesktopDatePicker
              open={true}
              value={selectedDate}
              onChange={handleDateChange}
              format={slashDateFormat}
              maxDate={maxDate}
              minDate={minDate || (disablePast ? new Date() : undefined)}
              disablePast={disablePast}
              slotProps={{
                popper: {
                  disablePortal: true,
                  style: { position: 'static' },
                },
                textField: {
                  sx: { display: 'none' },
                },
                desktopPaper: {
                  sx: {
                    borderRadius: '6px',
                    width: isDesktop ? '400px' : undefined,
                    '.PrivatePickersFadeTransitionGroup-root, div[aria-live="polite"]':
                      {
                        fontSize: isDesktop ? '1.375rem' : '1.125rem',
                        fontWeight: 400,
                        lineHeight: isDesktop ? '1.75rem' : '1.5rem',
                        color: '#1752AF',
                      },
                    '.MuiTypography-caption': {
                      fontSize: isDesktop ? '1.125rem' : '1rem',
                      fontWeight: 700,
                      lineHeight: isDesktop ? '1.5rem' : '1.25rem',
                      color: '#39546A',
                    },
                    '.MuiPickersDay-root': {
                      fontSize: isDesktop ? '1.125rem' : '1rem',
                      fontWeight: 400,
                      lineHeight: isDesktop ? '1.625rem' : '1.375rem',
                    },
                    '.PrivatePickersYear-root > button': {
                      fontSize: isDesktop ? '1.125rem' : '1rem',
                      fontWeight: 400,
                      lineHeight: isDesktop ? '1.625rem' : '1.375rem',
                    },
                    '.MuiPickersDay-today:not(.Mui-selected)': {
                      border: 'none !important',
                      backgroundColor: 'white !important',
                    },
                    '.Mui-selected': {
                      color: 'white',
                    },
                  },
                },
              }}
              slots={{
                openPickerIcon: (props) => (
                  <Avatar
                    {...props}
                    src={PurpleDateIcon}
                    alt=''
                    sx={{ width: '32px', height: '32px' }}
                  />
                ),
                leftArrowIcon: (props) => (
                  <Avatar
                    {...props}
                    src={LeftArrowDatePicker}
                    alt=''
                    sx={{ width: '32px', height: '32px' }}
                  />
                ),
                rightArrowIcon: (props) => (
                  <Avatar
                    {...props}
                    src={RightArrowDatePicker}
                    alt=''
                    sx={{ width: '32px', height: '32px' }}
                  />
                ),
                switchViewIcon: (props) => (
                  <Avatar
                    {...props}
                    src={AccordionArrowBlue}
                    alt=''
                    sx={{ width: '28px', height: '28px' }}
                  />
                ),
              }}
            />
          </LocalizationProvider>
        </div>
      </Modal>
    </>
  );
};
